import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff } from "lucide-react";

const StaffLogin = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // If already logged in, redirect to dashboard
  React.useEffect(() => {
    if (user) {
      navigate("/admin/overview", { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await response.json();
      if (response.ok && data.token) {
        login(data.token, data.user);
        toast.success(`Welcome back, ${data.user?.name || ""}`.trim());
        navigate("/admin/overview", { replace: true });
      } else {
        toast.error(data.detail || "Invalid credentials. Please try again.");
      }
    } catch (err) {
      toast.error("Unable to connect. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="staff-login">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-[480px] xl:w-[520px] bg-stone-950 flex-col justify-between p-12 xl:p-16">
        <div>
          <img src="/platemind-logo-new.png" alt="PlateMind" className="h-8 w-auto brightness-0 invert" />
        </div>
        <div>
          <h2 className="text-3xl xl:text-4xl font-extrabold text-white leading-tight tracking-tight">
            Staff
            <br />
            Dashboard
          </h2>
          <p className="mt-4 text-base text-white/50 leading-relaxed max-w-[360px]">
            Manage participants, consultations, meal plans, clinical reports and programme operations from one secure workspace.
          </p>
        </div>
        <p className="text-xs text-white/30">
          &copy; {new Date().getFullYear()} PlateMind. Authorised staff only.
        </p>
      </div>

      {/* Right panel - login form */}
      <div className="flex-1 flex items-center justify-center px-6 sm:px-12">
        <div className="w-full max-w-[400px]">
          {/* Mobile logo */}
          <div className="lg:hidden mb-10">
            <img src="/platemind-logo-new.png" alt="PlateMind" className="h-8 w-auto" />
          </div>

          <h1 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
            Sign in
          </h1>
          <p className="mt-2 text-base text-stone-500">
            Access the PlateMind staff dashboard.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-stone-700 mb-1.5">
                Email address
              </label>
              <Input
                id="email"
                type="email"
                placeholder="you@platemind.app"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 rounded-xl border-stone-300 text-base focus:border-[#FF7A00] focus:ring-[#FF7A00]/20"
                data-testid="login-email"
                autoComplete="email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-stone-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 rounded-xl border-stone-300 text-base pr-12 focus:border-[#FF7A00] focus:ring-[#FF7A00]/20"
                  data-testid="login-password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-xl text-base font-semibold"
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign in"
              )}
            </Button>
          </form>

          <p className="mt-8 text-xs text-stone-400 text-center leading-relaxed">
            This is a secure staff workspace. If you are a programme participant, please contact your dietitian through your usual communication channel.
          </p>
        </div>
      </div>
    </div>
  );
};

export default StaffLogin;
